import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography } from '@material-ui/core'
import { ProductForm } from '../../widgets/productForm'
import { useApi, useStore } from '../../../hooks/storeHook'
import { useBoolState, useIsMounted, useLazyEffect } from '../../../hooks/commonHooks'
import { ScreenLoader } from '../../../components/ScreenLoader'
import React, { useLayoutEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useCardPageMode } from './lib/useCardPageMode'
import { getIntegrationTypeName } from '../../../types/navStore'
import { integrationColors, integrationKeyByType, integrationTypeSlugs } from '../../../types/integrationTypeUtils'
import { loadMpCardNavContext } from '../../shared/lib/mpCardNavContext'
import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'

const resolveNavContext = (
  integrationType: IntegrationType,
  accountId: number | undefined
): { integrationType: IntegrationType; accountId: number } | null => {
  if (accountId != null) {
    return {integrationType, accountId}
  }

  return loadMpCardNavContext()
}

export const CardPage = observer(() => {
  const classes = useStyles()
  const {
    productStore: {setSelectedProductCard, setNewProductCard},
    homeNavRequired,
    setHomeNav
  } = useStore()

  const isMounted = useIsMounted()

  const integrationType = homeNavRequired.integrationType
  const accountId = homeNavRequired.accountId

  const [isLoading, , setLoaded] = useBoolState(true)
  const api = useApi()

  const {mode, id, productId} = useCardPageMode()

  useLayoutEffect(() => {
    if (accountId != null || mode === 'create') {
      return
    }

    const savedNav = loadMpCardNavContext()

    if (savedNav != null) {
      setHomeNav(savedNav.integrationType, savedNav.accountId)
    }
  }, [accountId, mode, setHomeNav])

  useLazyEffect(async () => {
    let shouldFinishLoading = true

    try {
      if (mode === 'create') {
        const result = await api.productCategory.getBaseAttributes()
        isMounted.current && setNewProductCard(result)

      } else if (mode === 'edit') {
        const navContext = resolveNavContext(integrationType, accountId)

        if (!navContext || !productId) {
          shouldFinishLoading = navContext == null
          return
        }

        if (accountId == null) {
          setHomeNav(navContext.integrationType, navContext.accountId)
        }

        const result = await api.mpCard.getProductCard({
          integrationType: navContext.integrationType,
          productId: Number(productId),
          accountId: navContext.accountId
        })

        isMounted.current && setSelectedProductCard(result)

      } else if (mode === 'import') {
        const navContext = resolveNavContext(integrationType, accountId)

        if (!navContext) {
          shouldFinishLoading = true
          return
        }

        if (!productId || !id) {
          throw new Error(`Incorrect import route`)
        }

        if (accountId == null) {
          setHomeNav(navContext.integrationType, navContext.accountId)
        }

        const result = await api.getCardsForImport(
          id, productId, integrationTypeSlugs[navContext.integrationType])

        isMounted.current && setSelectedProductCard(result.dbCard, result.apiCard)

      } else {
        throw new Error(`Unknown page mode`)
      }
    } catch (error) {
      console.error('CardPage load failed:', error)
    } finally {
      if (shouldFinishLoading && isMounted.current) {
        setLoaded()
      }
    }
  }, [mode, id, productId, accountId, integrationType])

  if (isLoading) {
    return <ScreenLoader/>
  }

  return <Box className={classes.wrapper}>
    {mode === 'import' && <Typography variant={'h1'} className={classes.title}>Импорт
      карточки {id} в <span style={{
        color: integrationColors[integrationKeyByType[
          integrationType]]
      }}>{getIntegrationTypeName(
        integrationType)}</span></Typography>}
    <ProductForm/>
  </Box>
})

const useStyles = makeStyles(
  () => ({
    wrapper: {
      width: '100%',
      maxWidth: '100vw',
      padding: '50px',
      paddingTop: '30px',
      boxSizing: 'border-box'
    },
    title: {
      fontSize: 24,
      marginBottom: 20,
      textAlign: 'center',
      fontWeight: 'normal'
    }
  }))
